import React, { useState, useEffect } from 'react';
import { Search, CheckCircle2, XCircle, Circle, Info } from 'lucide-react';
import fnolStateService from '../../services/fnolStateService';
import './TriagingPage.css';
import AssignmentDecisionLogic from './AssignmentDecisionLogic';
import {
    triageClaim,
    applyOverride,
    LineOfBusiness,
    ScopeCode,
    BusinessSegment,
    adjusterPool,
    handlerPool
} from '../../services/triagingService';
import { fnolAPI } from '../../api';

const Req = () => <span className="text-danger ms-1">*</span>;

const ASSIGNMENT_API_BASE_URL = 'https://fnolbackend.alphatorana.com/api/assignment';

const RegionSelect = ({ isEditMode, placeholder = "Select Region", value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    // Sample regions list
    const regions = [
        "United States", "United Kingdom", "Canada", "Australia",
        "India", "Germany", "France", "Japan", "Brazil", "China",
        "South Africa", "Mexico", "Italy", "Spain", "Russia"
    ];

    const filteredRegions = regions.filter(r =>
        r.toLowerCase().includes((value || '').toLowerCase())
    );

    const handleSelect = (region) => {
        onChange(region);
        setIsOpen(false);
    };

    return (
        <div className="position-relative">
            <div className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" style={{ pointerEvents: 'none', zIndex: 5 }}>
                <Search size={14} />
            </div>
            <input
                type="text"
                className="form-control form-control-sm triage-input"
                style={{ paddingLeft: '30px' }}
                placeholder={placeholder}
                disabled={!isEditMode}
                value={value || ''}
                onChange={(e) => {
                    onChange(e.target.value);
                    setIsOpen(true);
                }}
                onFocus={() => isEditMode && setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            />
            {isOpen && isEditMode && filteredRegions.length > 0 && (
                <ul className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto', top: '100%' }}>
                    {filteredRegions.map((region) => (
                        <li
                            key={region}
                            className="list-group-item list-group-item-action py-1 px-2 small"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelect(region)}
                            onMouseDown={(e) => e.preventDefault()}
                        >
                            {region}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const TriagingPage = ({ selected, isCompleted, setIsCompleted, onPrevious, onNext, hasClaimId }) => {
    const handleComplete = async () => {
        if (!selected?.id) return;
        // Prevent status changes if claim ID exists
        if (hasClaimId) {
            console.log('⚠️ Cannot change completion status - claim already exists');
            return;
        }
        const newState = !isCompleted;
        setIsCompleted(newState);

        // Update API state
        try {
            await fnolStateService.updateState(selected.id, {
                Triaging: newState ? 'completed' : 'visited'
            });
            console.log('✅ Triaging completion state updated in API');
        } catch (error) {
            console.error('Failed to update Triaging state in API:', error);
        }

    };

    // Input Parameters State - Initialize with data from API if available
    const [inputParams, setInputParams] = useState({
        lob: LineOfBusiness.PROPERTY,
        region: '',
        estimatedLoss: '',
        estimatedLossCurrency: '',
        gleFlag: '',
        industryCode: '',
        scopeCode: '',
        businessSegment: ''
    });

    const [fnolData, setFnolData] = useState(null);
    const [loadingFnolData, setLoadingFnolData] = useState(false);

    // Fetch FNOL data from API when selected changes
    useEffect(() => {
        const fetchFnolData = async () => {
            if (!selected?.id) {
                setFnolData(null);
                return;
            }

            setLoadingFnolData(true);
            try {
                const response = await fnolAPI.getFNOL(selected.id);
                if (response.success) {
                    const data = response.data;
                    setFnolData(data);

                    // Normalize GLE value - treat null/undefined/empty/N/A as empty string
                    const normalizeGLEValue = (gleValue) => {
                        if (!gleValue) return '';
                        const gleStr = String(gleValue).trim();
                        const gleLower = gleStr.toLowerCase();
                        if (gleLower === 'null' || gleLower === 'undefined' || gleLower === 'n/a' || gleLower === 'na' || gleLower === 'none') {
                            return '';
                        }
                        return gleStr;
                    };

                    // Update input parameters with fetched data
                    setInputParams(prev => ({
                        ...prev,
                        lob: LineOfBusiness.PROPERTY,
                        region: data.loss_location || prev.region,
                        estimatedLoss: data.estimated_loss_amount || prev.estimatedLoss,
                        estimatedLossCurrency: data.estimated_loss_currency || prev.estimatedLossCurrency,
                        gleFlag: normalizeGLEValue(data.global_loss_details)
                    }));
                }
            } catch (error) {
                console.error('Failed to fetch FNOL data:', error);
            } finally {
                setLoadingFnolData(false);
            }
        };

        fetchFnolData();
    }, [selected?.id]);

    // Triaging Results State
    const [triageResult, setTriageResult] = useState(null);
    const [rulesFired, setRulesFired] = useState([]);
    const [resolvedRegion, setResolvedRegion] = useState('');

    const [assignmentData, setAssignmentData] = useState({
        adjuster: null,
        handler: null
    });
    const [initialAssignmentData, setInitialAssignmentData] = useState(null);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [modalComment, setModalComment] = useState('');

    const [adjusterRegion, setAdjusterRegion] = useState('');
    const [handlerRegion, setHandlerRegion] = useState('');

    const [isEditMode, setIsEditMode] = useState(false);
    const [loadingAssignment, setLoadingAssignment] = useState(false);
    const [assignmentError, setAssignmentError] = useState(null);

    // Cache for assignment data to prevent redundant API calls
    const [assignmentCache, setAssignmentCache] = useState({});

    // Dynamic pools that include API-returned people not in the static pool
    const [dynamicAdjusterPool, setDynamicAdjusterPool] = useState(adjusterPool);
    const [dynamicHandlerPool, setDynamicHandlerPool] = useState(handlerPool);

    // Load existing assignment when FNOL is selected
    useEffect(() => {
        const loadExistingAssignment = async () => {
            if (!selected?.id) return;

            // Check cache first
            if (assignmentCache[selected.id]) {
                console.log('Using cached assignment data for FNOL:', selected.id);
                const cached = assignmentCache[selected.id];
                setAssignmentData(cached.assignmentData);
                setInitialAssignmentData(cached.assignmentData);
                setAdjusterRegion(cached.adjusterRegion);
                setHandlerRegion(cached.handlerRegion);
                setResolvedRegion(cached.resolvedRegion);
                if (cached.inputParams) {
                    setInputParams(prev => ({
                        ...prev,
                        industryCode: cached.inputParams.industryCode,
                        scopeCode: cached.inputParams.scopeCode,
                        businessSegment: cached.inputParams.businessSegment
                    }));
                }
                setDynamicAdjusterPool(cached.dynamicAdjusterPool || adjusterPool);
                setDynamicHandlerPool(cached.dynamicHandlerPool || handlerPool);
                setLoadingAssignment(false);
                return;
            }

            setLoadingAssignment(true);
            setAssignmentError(null);

            try {
                const response = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                    headers: {
                        'ngrok-skip-browser-warning': 'true'
                    }
                });

                if (response.status === 404) {
                    // No assignment exists yet, reset state
                    console.log('No assignment found for this FNOL');
                    setAssignmentData({ adjuster: null, handler: null });
                    setInitialAssignmentData(null);
                    setAdjusterRegion('');
                    setHandlerRegion('');
                    setResolvedRegion('');
                    setTriageResult(null);
                    setRulesFired([]);
                    setDynamicAdjusterPool(adjusterPool);
                    setDynamicHandlerPool(handlerPool);
                    setLoadingAssignment(false);
                    return;
                }

                if (!response.ok) {
                    throw new Error('Failed to load assignment');
                }

                const data = await response.json();

                // Map API response to component state
                let adjuster = adjusterPool.find(a => a.name === data.claim_adjuster);
                let handler = handlerPool.find(h => h.name === data.claim_handler);

                let updatedAdjusterPool = adjusterPool;
                let updatedHandlerPool = handlerPool;

                // If adjuster not in pool, add them dynamically
                if (!adjuster && data.claim_adjuster) {
                    adjuster = {
                        name: data.claim_adjuster,
                        region: data.adjuster_region,
                        level: 'UNKNOWN'
                    };
                    updatedAdjusterPool = [...adjusterPool, adjuster];
                    setDynamicAdjusterPool(updatedAdjusterPool);
                } else {
                    setDynamicAdjusterPool(adjusterPool);
                }

                // If handler not in pool, add them dynamically
                if (!handler && data.claim_handler) {
                    handler = {
                        name: data.claim_handler,
                        region: data.handler_region,
                        type: 'UNKNOWN'
                    };
                    updatedHandlerPool = [...handlerPool, handler];
                    setDynamicHandlerPool(updatedHandlerPool);
                } else {
                    setDynamicHandlerPool(handlerPool);
                }

                const loadedAssignment = {
                    adjuster: adjuster,
                    handler: handler
                };

                setAssignmentData(loadedAssignment);
                setInitialAssignmentData(loadedAssignment);
                setResolvedRegion(data.adjuster_region || '');
                setAdjusterRegion(data.adjuster_region || '');
                setHandlerRegion(data.handler_region || '');

                // Load optional fields if they exist
                const updatedInputParams = {
                    industryCode: data.industry,
                    scopeCode: data.scope_code,
                    businessSegment: data.business_segment
                };

                setInputParams(prev => ({
                    ...prev,
                    industryCode: data.industry || prev.industryCode,
                    scopeCode: data.scope_code || prev.scopeCode,
                    businessSegment: data.business_segment || prev.businessSegment
                }));

                // Cache the loaded data
                setAssignmentCache(prev => ({
                    ...prev,
                    [selected.id]: {
                        assignmentData: loadedAssignment,
                        adjusterRegion: data.adjuster_region || '',
                        handlerRegion: data.handler_region || '',
                        resolvedRegion: data.adjuster_region || '',
                        inputParams: updatedInputParams,
                        dynamicAdjusterPool: updatedAdjusterPool,
                        dynamicHandlerPool: updatedHandlerPool
                    }
                }));

            } catch (error) {
                console.error('Error loading assignment:', error);
                setAssignmentError(error.message);
            } finally {
                setLoadingAssignment(false);
            }
        };

        loadExistingAssignment();
    }, [selected?.id, selected?.data, assignmentCache]);

    // Industry options
    const industryOptions = [
        'Construction',
        'Mining',
        'Oil/Gas',
        'Aviation',
        'Healthcare',
        'Maritime',
        'Shipping',
        'Manufacturing',
        'Transportation',
        'Retail',
        'Hospitality',
        'Agriculture',
        'Information Technology',
        'Financial Services',
        'Real Estate',
        'Other'
    ];

    // Scope code options
    const scopeCodeOptions = [
        { value: ScopeCode.SC1, label: 'SC1 - Standard' },
        { value: ScopeCode.SC2, label: 'SC2 - Moderate' },
        { value: ScopeCode.SC3, label: 'SC3 - Complex' },
        { value: ScopeCode.SC4, label: 'SC4 - Very Complex' }
    ];

    // Business segment options
    const businessSegmentOptions = [
        { value: BusinessSegment.SMALL, label: 'Small Business' },
        { value: BusinessSegment.MID_CORP, label: 'Mid-Market Corporate' },
        { value: BusinessSegment.LCR, label: 'Large Corporate (LCR)' }
    ];

    // Run triaging when "Assignment" button is clicked
    const handleRunTriaging = async () => {
        if (!inputParams.region || !inputParams.estimatedLoss || !inputParams.industryCode || !inputParams.scopeCode || !inputParams.businessSegment) {
            alert('Please fill in all required fields (Region, Loss Estimation, Industrial Classification, Scope Code, and Business Segment)');
            return;
        }

        // Normalize GLE flag: treat empty/null/N/A as false, any other value as true
        const normalizeGLEFlag = (gleValue) => {
            const gleStr = String(gleValue || '').trim().toLowerCase();
            if (!gleStr || gleStr === '' || gleStr === 'null' || gleStr === 'undefined') return false;
            if (gleStr === 'n/a' || gleStr === 'na' || gleStr === 'none') return false;
            if (gleStr === 'false' || gleStr === 'no') return false;
            return true;
        };

        const claim = {
            claimId: selected?.id || 'CLAIM-' + Date.now(),
            lob: inputParams.lob,
            estimatedLoss: parseFloat(inputParams.estimatedLoss),
            lossCountry: inputParams.region,
            industryCode: inputParams.industryCode,
            scopeCode: inputParams.scopeCode,
            gleFlag: normalizeGLEFlag(inputParams.gleFlag),
            gleFlagDescription: inputParams.gleFlag || '',
            businessSegment: inputParams.businessSegment
        };

        const result = triageClaim(claim);
        setTriageResult(result);
        setRulesFired(result.rulesFired);
        setResolvedRegion(result.claim.region); // Store the resolved region
        setAdjusterRegion(result.claim.region);
        setHandlerRegion(result.claim.region);

        const newAssignment = {
            adjuster: result.adjuster,
            handler: result.handler
        };

        setAssignmentData(newAssignment);

        // IMPORTANT: Do NOT overwrite initialAssignmentData when in edit mode.
        // The snapshot taken at handleEdit() must remain intact so that Cancel
        // can always restore the state to what it was before the user clicked Edit.
        if (!isEditMode) {
            setInitialAssignmentData(newAssignment);
        }

        // Reset dynamic pools to static pools when running auto-assign
        setDynamicAdjusterPool(adjusterPool);
        setDynamicHandlerPool(handlerPool);

        // IMPORTANT: Do NOT write to the API here when in edit mode.
        // The API should only be updated when the user explicitly clicks "Save Changes"
        // and confirms via the modal. This ensures that a page reload after Cancel
        // always shows the last explicitly saved values, not the Auto Assign preview.
        if (!isEditMode && selected?.id) {
            try {
                const response = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'ngrok-skip-browser-warning': 'true'
                    },
                    body: JSON.stringify({
                        claim_adjuster: result.adjuster.name,
                        adjuster_region: adjusterRegion || result.claim.region,
                        claim_handler: result.handler.name,
                        handler_region: handlerRegion || result.claim.region,
                        industry: inputParams.industryCode || null,
                        scope_code: inputParams.scopeCode || null,
                        business_segment: inputParams.businessSegment || null
                    })
                });

                if (response.status === 404) {
                    // Assignment doesn't exist, create it first with POST
                    const createResponse = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'ngrok-skip-browser-warning': 'true'
                        },
                        body: JSON.stringify({
                            claim_adjuster: result.adjuster.name,
                            adjuster_region: adjusterRegion || result.claim.region,
                            claim_handler: result.handler.name,
                            handler_region: handlerRegion || result.claim.region,
                            industry: inputParams.industryCode || null,
                            scope_code: inputParams.scopeCode || null,
                            business_segment: inputParams.businessSegment || null
                        })
                    });

                    if (createResponse.ok) {
                        const savedData = await createResponse.json();
                        console.log('Assignment created successfully:', savedData);
                    } else if (createResponse.status === 400) {
                        console.log('Assignment already exists');
                    } else {
                        throw new Error('Failed to create assignment');
                    }
                } else if (!response.ok) {
                    throw new Error('Failed to update assignment');
                } else {
                    const savedData = await response.json();
                    console.log('Assignment updated successfully:', savedData);

                    // Update cache with new assignment data
                    setAssignmentCache(prev => ({
                        ...prev,
                        [selected.id]: {
                            assignmentData: newAssignment,
                            adjusterRegion: adjusterRegion || result.claim.region,
                            handlerRegion: handlerRegion || result.claim.region,
                            resolvedRegion: result.claim.region,
                            inputParams: {
                                industryCode: inputParams.industryCode,
                                scopeCode: inputParams.scopeCode,
                                businessSegment: inputParams.businessSegment
                            },
                            dynamicAdjusterPool: adjusterPool,
                            dynamicHandlerPool: handlerPool
                        }
                    }));
                }
            } catch (error) {
                console.error('Error saving assignment:', error);
                alert('Assignment updated locally but failed to save to server: ' + error.message);
            }
        }
    };

    const handleEdit = () => {
        if (hasClaimId) return; // Prevent editing if claim exists
        // Snapshot ALL editable state before entering edit mode
        setInitialAssignmentData({
            ...assignmentData,
            adjusterRegion: adjusterRegion,
            handlerRegion: handlerRegion,
            // Also snapshot the input params dropdowns so Cancel can restore them
            industryCode: inputParams.industryCode,
            scopeCode: inputParams.scopeCode,
            businessSegment: inputParams.businessSegment,
            // Snapshot the triage result + rules so AssignmentDecisionLogic
            // goes back to its exact pre-edit state on Cancel
            triageResult: triageResult,
            rulesFired: rulesFired
        });
        setIsEditMode(true);
    };

    const handleCancel = () => {
        if (initialAssignmentData) {
            // Restore assignment (adjuster + handler)
            setAssignmentData({
                adjuster: initialAssignmentData.adjuster,
                handler: initialAssignmentData.handler
            });
            // Restore regions
            if (initialAssignmentData.adjusterRegion !== undefined) {
                setAdjusterRegion(initialAssignmentData.adjusterRegion);
            }
            if (initialAssignmentData.handlerRegion !== undefined) {
                setHandlerRegion(initialAssignmentData.handlerRegion);
            }
            // Restore input params dropdowns (Industrial Classification, Scope Code, Business Segment)
            setInputParams(prev => ({
                ...prev,
                industryCode: initialAssignmentData.industryCode ?? prev.industryCode,
                scopeCode: initialAssignmentData.scopeCode ?? prev.scopeCode,
                businessSegment: initialAssignmentData.businessSegment ?? prev.businessSegment
            }));
            // Restore AssignmentDecisionLogic to its exact pre-edit state.
            // If it was hidden before Edit was clicked (rulesFired was empty),
            // it will hide again. If it was visible, it reverts to the original result.
            setTriageResult(initialAssignmentData.triageResult ?? null);
            setRulesFired(initialAssignmentData.rulesFired ?? []);
        }
        setIsEditMode(false);
    };

    const handleSubmit = () => {
        const isAssignmentChanged = JSON.stringify(assignmentData) !== JSON.stringify(initialAssignmentData);
        if (isAssignmentChanged) {
            setShowConfirmationModal(true);
        } else {
            setIsEditMode(false);
        }
    };

    const handleModalClose = () => {
        setShowConfirmationModal(false);
    }

    const handleModalSave = async () => {
        // Apply override
        if (triageResult && modalComment.trim()) {
            try {
                applyOverride(
                    triageResult.claimId,
                    assignmentData,
                    'current-user', // You can replace with actual user ID
                    modalComment
                );
                setInitialAssignmentData({ ...assignmentData });
            } catch (error) {
                console.error('Override error:', error);
            }
        }

        // Update assignment in API
        if (selected?.id) {
            try {
                const response = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'ngrok-skip-browser-warning': 'true'
                    },
                    body: JSON.stringify({
                        claim_adjuster: assignmentData.adjuster?.name,
                        adjuster_region: adjusterRegion,
                        claim_handler: assignmentData.handler?.name,
                        handler_region: handlerRegion,
                        industry: inputParams.industryCode || null,
                        scope_code: inputParams.scopeCode || null,
                        business_segment: inputParams.businessSegment || null
                    })
                });

                if (response.status === 404) {
                    // Assignment doesn't exist, create it first with POST
                    const createResponse = await fetch(`${ASSIGNMENT_API_BASE_URL}/${selected.id}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'ngrok-skip-browser-warning': 'true'
                        },
                        body: JSON.stringify({
                            claim_adjuster: assignmentData.adjuster?.name,
                            adjuster_region: adjusterRegion,
                            claim_handler: assignmentData.handler?.name,
                            handler_region: handlerRegion,
                            industry: inputParams.industryCode || null,
                            scope_code: inputParams.scopeCode || null,
                            business_segment: inputParams.businessSegment || null
                        })
                    });

                    if (createResponse.ok) {
                        const savedData = await createResponse.json();
                        console.log('Assignment created successfully via Edit:', savedData);
                    } else if (createResponse.status === 400) {
                        console.log('Assignment already exists');
                    } else {
                        throw new Error('Failed to create assignment');
                    }
                } else if (!response.ok) {
                    throw new Error('Failed to update assignment');
                } else {
                    const updatedData = await response.json();
                    console.log('Assignment updated successfully:', updatedData);
                }

                // Update cache with new data
                setAssignmentCache(prev => ({
                    ...prev,
                    [selected.id]: {
                        assignmentData: assignmentData,
                        adjusterRegion: adjusterRegion,
                        handlerRegion: handlerRegion,
                        resolvedRegion: adjusterRegion,
                        inputParams: {
                            industryCode: inputParams.industryCode,
                            scopeCode: inputParams.scopeCode,
                            businessSegment: inputParams.businessSegment
                        },
                        dynamicAdjusterPool: dynamicAdjusterPool,
                        dynamicHandlerPool: dynamicHandlerPool
                    }
                }));

            } catch (error) {
                console.error('Error updating assignment:', error);
                alert('Failed to update assignment: ' + error.message);
                setShowConfirmationModal(false);
                setIsEditMode(false);
                setModalComment('');
                return;
            }
        }

        setShowConfirmationModal(false);
        setIsEditMode(false);
        setModalComment('');
    }

    const [comment, setComment] = useState('');
    const commentTextareaRef = React.useRef(null);

    // Comment formatting functions
    const applyFormatting = (prefix, suffix = '') => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = comment.substring(start, end);
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(end);

        const newText = beforeText + prefix + selectedText + suffix + afterText;
        setComment(newText);

        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleBold = () => applyFormatting('**', '**');
    const handleItalic = () => applyFormatting('*', '*');
    const handleUnderline = () => applyFormatting('<u>', '</u>');
    const handleStrikethrough = () => applyFormatting('~~', '~~');
    const handleBulletList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n• ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };
    const handleNumberedList = () => {
        const textarea = commentTextareaRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = comment.substring(0, start);
        const afterText = comment.substring(start);
        const newText = beforeText + '\n1. ';
        setComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };

    // Modal Comment Logic
    const modalCommentInputRef = React.useRef(null);

    const applyModalFormatting = (prefix, suffix = '') => {
        const textarea = modalCommentInputRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = modalComment.substring(start, end);
        const beforeText = modalComment.substring(0, start);
        const afterText = modalComment.substring(end);

        const newText = beforeText + prefix + selectedText + suffix + afterText;
        setModalComment(newText);

        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleModalBold = () => applyModalFormatting('**', '**');
    const handleModalItalic = () => applyModalFormatting('*', '*');
    const handleModalUnderline = () => applyModalFormatting('<u>', '</u>');
    const handleModalStrikethrough = () => applyModalFormatting('~~', '~~');

    const handleModalBulletList = () => {
        const textarea = modalCommentInputRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = modalComment.substring(0, start);
        const afterText = modalComment.substring(start);
        const newText = beforeText + '\n• ';
        setModalComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };

    const handleModalNumberedList = () => {
        const textarea = modalCommentInputRef.current;
        if (!textarea) return;
        const start = textarea.selectionStart;
        const beforeText = modalComment.substring(0, start);
        const afterText = modalComment.substring(start);
        const newText = beforeText + '\n1. ';
        setModalComment(newText + afterText);
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(newText.length, newText.length);
        }, 0);
    };

    const isFormValid = (
        inputParams?.lob &&
        // inputParams?.region &&
        // inputParams?.estimatedLoss &&
        // inputParams?.industryCode &&
        inputParams?.scopeCode &&
        inputParams?.businessSegment &&
        assignmentData?.adjuster &&
        assignmentData?.handler
    );

    return (
        <div
            className="h-100 overflow-auto auto-hide-scrollbar"
        >
            <div className="card border-0 bg-transparent">
                <div className="card-body p-0">
                    {/* Input Parameters Section */}
                    <section className="card triage-card mb-2 px-2">
                        <div className="card-body">
                            <h5 className="triage-card-title mb-2">Input Parameters</h5>
                            <div className="ps-2">
                                <div className="row g-2 mb-2">
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">
                                            Line of Business (LOB) <Req />
                                            <small className="text-muted ms-1"></small>
                                        </label>
                                        <input
                                            className="form-control form-control-sm triage-input"
                                            value={inputParams.lob}
                                            disabled
                                        />
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">
                                            Region <Req />
                                            {inputParams.region && fnolData?.loss_location && (
                                                <small className="text-success ms-1"></small>
                                            )}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm triage-input"
                                            placeholder="Select Region"
                                            value={inputParams.region || ''}
                                            disabled
                                            readOnly
                                        />
                                        {fnolData?.loss_location && (
                                            <small className="text-muted d-block mt-1">
                                                Loss Location: {fnolData.loss_location}
                                            </small>
                                        )}
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">
                                            Loss Estimation <Req />
                                            {inputParams.estimatedLoss && fnolData?.estimated_loss_amount && (
                                                <small className="text-success ms-1"></small>
                                            )}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm triage-input"
                                            placeholder="Enter amount"
                                            value={inputParams.estimatedLoss ? inputParams.estimatedLoss.toLocaleString() : ''}
                                            disabled
                                            readOnly
                                        />
                                        {fnolData?.estimated_loss_currency && (
                                            <small className="text-muted d-block mt-1">
                                                Currency: {fnolData.estimated_loss_currency}
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-2">
                                    <label className="form-label triage-label">Global Loss Event</label>
                                    <input
                                        className="form-control form-control-sm triage-input"
                                        placeholder="Enter Global Loss Event"
                                        value={inputParams.gleFlag}
                                        onChange={(e) => setInputParams({ ...inputParams, gleFlag: e.target.value })}
                                        disabled
                                    />
                                </div>

                                <div className="row g-2 mb-2">
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">Industrial Classification <Req /></label>
                                        <select
                                            className="form-select form-select-sm triage-select"
                                            value={inputParams.industryCode}
                                            onChange={(e) => setInputParams({ ...inputParams, industryCode: e.target.value })}
                                            disabled={!isEditMode}
                                        >
                                            <option value="">Select Industry</option>
                                            {industryOptions.map(industry => (
                                                <option key={industry} value={industry}>{industry}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">Scope Code <Req /></label>
                                        <select
                                            className="form-select form-select-sm triage-select"
                                            value={inputParams.scopeCode}
                                            onChange={(e) => setInputParams({ ...inputParams, scopeCode: e.target.value })}
                                            disabled={!isEditMode}
                                        >
                                            <option value="">Select Scope Code</option>
                                            {scopeCodeOptions.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-4">
                                        <label className="form-label triage-label">Business Segment <Req /></label>
                                        <select
                                            className="form-select form-select-sm triage-select"
                                            value={inputParams.businessSegment}
                                            onChange={(e) => setInputParams({ ...inputParams, businessSegment: e.target.value })}
                                            disabled={!isEditMode}
                                        >
                                            <option value="">Select Business Segment</option>
                                            {businessSegmentOptions.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-end mt-3">
                                    <button
                                        type="button"
                                        className="btn triage-action-btn triage-btn-primary"
                                        style={(hasClaimId || isCompleted || !inputParams.industryCode || !inputParams.scopeCode || !inputParams.businessSegment) ? {
                                            borderColor: '#CCCCCC',
                                            color: 'white',
                                            backgroundColor: '#006BB3',
                                            cursor: 'not-allowed'
                                        } : {}}
                                        onClick={handleRunTriaging}
                                        disabled={hasClaimId || !inputParams.industryCode || !inputParams.scopeCode || !inputParams.businessSegment}
                                        title={hasClaimId ? "Auto Assign is disabled for existing claims" : (!inputParams.industryCode || !inputParams.scopeCode || !inputParams.businessSegment) ? "Please select all required fields" : ""}
                                    >
                                        Auto Assign
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>


                    <AssignmentDecisionLogic
                        rulesFired={rulesFired}
                        inputParams={inputParams}
                        triageResult={triageResult}
                        assignmentData={assignmentData}
                        adjusterRegion={adjusterRegion}
                        handlerRegion={handlerRegion}
                    />

                    {/* Assignment Section */}
                    <section className="card triage-card mb-2 px-2">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="triage-card-title mb-0">Assignment</h5>
                                {loadingAssignment && (
                                    <small className="text-primary">Loading assignment...</small>
                                )}
                                {assignmentError && (
                                    <small className="text-danger">Error: {assignmentError}</small>
                                )}
                            </div>
                            <div className="ps-2">
                                {/* Region Display Row */}
                                <div className="row g-2 mb-2">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label triage-label">Adjuster Region</label>
                                        <RegionSelect
                                            isEditMode={isEditMode}
                                            placeholder="Run assignment to see region"
                                            value={adjusterRegion}
                                            onChange={(val) => setAdjusterRegion(val)}
                                        />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label triage-label">Handler Region</label>
                                        <RegionSelect
                                            isEditMode={isEditMode}
                                            placeholder="Run assignment to see region"
                                            value={handlerRegion}
                                            onChange={(val) => setHandlerRegion(val)}
                                        />
                                    </div>
                                </div>

                                {/* Adjuster and Handler Selection Row */}
                                <div className="row g-2 mb-3">
                                    <div className="col-12 col-md-6">
                                        <label className="form-label triage-label">Claim Adjuster <Req /></label>
                                        <select
                                            className="form-select form-select-sm triage-select"
                                            disabled={!isEditMode}
                                            value={assignmentData.adjuster?.name || ''}
                                            onChange={(e) => {
                                                const selected = dynamicAdjusterPool.find(a => a.name === e.target.value);
                                                setAssignmentData({ ...assignmentData, adjuster: selected });
                                            }}
                                        >
                                            <option value="">Select Adjuster</option>
                                            {dynamicAdjusterPool.map(adjuster => (
                                                <option key={adjuster.name} value={adjuster.name}>
                                                    {adjuster.name} ({adjuster.level} - {adjuster.region})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <label className="form-label triage-label">Claim Handler <Req /></label>
                                        <select
                                            className="form-select form-select-sm triage-select"
                                            disabled={!isEditMode}
                                            value={assignmentData.handler?.name || ''}
                                            onChange={(e) => {
                                                const selected = dynamicHandlerPool.find(h => h.name === e.target.value);
                                                setAssignmentData({ ...assignmentData, handler: selected });
                                            }}
                                        >
                                            <option value="">Select Handler</option>
                                            {dynamicHandlerPool.map(handler => (
                                                <option key={handler.name} value={handler.name}>
                                                    {handler.name} ({handler.type} - {handler.region})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Comments Section */}
                    <section className="card mb-2" style={{ borderRadius: '10px' }}>
                        <div className="card-body">
                            {/* Label and Toolbar Row */}
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <label className="form-label fw-bold mb-0">
                                    Comments
                                </label>
                                {/* Toolbar */}
                                <div className="d-flex gap-1 align-items-center flex-wrap">
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bold" onClick={handleBold} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <strong style={{ fontSize: '0.85rem' }}>B</strong>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Italic" onClick={handleItalic} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <em style={{ fontSize: '0.85rem' }}>I</em>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Underline" onClick={handleUnderline} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <u style={{ fontSize: '0.85rem' }}>U</u>
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Strikethrough" onClick={handleStrikethrough} style={{ width: '1.75rem', height: '1.75rem' }}>
                                        <s style={{ fontSize: '0.85rem' }}>S</s>
                                    </button>
                                    <div className="vr" style={{ height: '1.25rem' }}></div>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bullet List" onClick={handleBulletList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ≡
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Numbered List" onClick={handleNumberedList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ≣
                                    </button>
                                    <div className="vr" style={{ height: '1.25rem' }}></div>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Link" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        🔗
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Image" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        🖼
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 p-1" title="More" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                                        ⋯
                                    </button>
                                </div>
                            </div>

                            {/* Text Area */}
                            <textarea
                                ref={commentTextareaRef}
                                className="form-control"
                                rows="6"
                                placeholder=""
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                style={{
                                    resize: 'none',
                                    borderRadius: '6px',
                                    borderColor: '#ced4da'
                                }}
                            ></textarea>
                        </div>
                    </section>

                    {/* Action Buttons */}
                    <section className="card triage-card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between gap-2 action-buttons-container">
                                <button
                                    type="button"
                                    className="btn triage-action-btn triage-btn-outline"
                                    onClick={onPrevious}
                                >
                                    Previous
                                </button>
                                <div className="d-flex gap-2 action-buttons-container">
                                    {!isEditMode ? (
                                        <>
                                            <button
                                                type="button"
                                                className="btn triage-action-btn triage-btn-outline"
                                                style={hasClaimId ? {
                                                    borderColor: '#CCCCCC',
                                                    color: '#CCCCCC',
                                                    backgroundColor: 'transparent',
                                                    cursor: 'not-allowed'
                                                } : {}}
                                                onClick={handleEdit}
                                                disabled={hasClaimId}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn triage-action-btn ${isCompleted ? 'btn-success' : 'triage-btn-primary'}`}
                                                style={{
                                                    backgroundColor: !isFormValid ? '#CCCCCC' : (isCompleted ? '#006BB3' : undefined),
                                                    borderColor: !isFormValid ? '#CCCCCC' : '#006BB3',
                                                    color: !isFormValid ? 'white' : (isCompleted ? 'white' : undefined),
                                                    cursor: isFormValid ? 'pointer' : 'not-allowed'
                                                }}
                                                onClick={handleComplete}
                                                disabled={!isFormValid || hasClaimId}
                                            >
                                                {isCompleted ? (
                                                    <>
                                                        <span style={{ marginRight: '6px' }}>✓</span>
                                                        Completed
                                                    </>
                                                ) : (
                                                    'Complete'
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                className="btn triage-action-btn triage-btn-primary"
                                                onClick={onNext}
                                            >
                                                Next
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="btn triage-action-btn"
                                                style={{
                                                    backgroundColor: 'white',
                                                    borderColor: '#006BB3',
                                                    color: '#006BB3'
                                                }}
                                                onClick={handleCancel}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn triage-action-btn triage-btn-primary"
                                                onClick={handleSubmit}
                                            >
                                                <i className="bi bi-check-circle me-1"></i>
                                                Save Changes
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div >
            <ConfirmationModal
                show={showConfirmationModal}
                onClose={handleModalClose}
                onSave={handleModalSave}
                comment={modalComment}
                setComment={setModalComment}
                textareaRef={modalCommentInputRef}
                handleBold={handleModalBold}
                handleItalic={handleModalItalic}
                handleUnderline={handleModalUnderline}
                handleStrikethrough={handleModalStrikethrough}
                handleBulletList={handleModalBulletList}
                handleNumberedList={handleModalNumberedList}
            />
        </div >
    );
};

export default TriagingPage;

const ConfirmationModal = ({
    show,
    onClose,
    onSave,
    comment,
    setComment,
    textareaRef,
    handleBold,
    handleItalic,
    handleUnderline,
    handleStrikethrough,
    handleBulletList,
    handleNumberedList
}) => {
    if (!show) return null;

    return (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1050
            }}
        >
            <div
                className="card border-0 shadow-lg"
                style={{
                    width: '95%',
                    maxWidth: '600px',
                    borderRadius: '20px',
                    backgroundColor: 'white'
                }}
            >
                <div className="card-body p-4 text-center">
                    {/* Icon */}
                    <div className="mb-3 d-flex justify-content-center">
                        <div
                            className="d-flex justify-content-center align-items-center text-white fw-bold"
                            style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: '#006BB3',
                                fontSize: '40px'
                            }}
                        >
                            !
                        </div>
                    </div>

                    {/* Title */}
                    <h5 className="fw-bold mb-4 px-4" style={{ color: 'black', fontSize: '18px' }}>
                        If you save, all your data will be reset. Do you wish to override the Claim Handler and Claim Adjuster Details?
                    </h5>

                    {/* Rich Text Editor */}
                    <div className="text-start mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label fw-bold small mb-0">
                                Comments <Req />
                            </label>
                            {/* Toolbar */}
                            <div className="d-flex gap-1 align-items-center">
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleBold} style={{ width: '28px', height: '28px' }}><strong style={{ fontSize: '0.85rem' }}>B</strong></button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleItalic} style={{ width: '28px', height: '28px' }}><em style={{ fontSize: '0.85rem' }}>I</em></button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleUnderline} style={{ width: '28px', height: '28px' }}><u style={{ fontSize: '0.85rem' }}>U</u></button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleStrikethrough} style={{ width: '28px', height: '28px' }}><s style={{ fontSize: '0.85rem' }}>S</s></button>
                                <div className="vr" style={{ height: '16px' }}></div>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleBulletList} style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>≡</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" onClick={handleNumberedList} style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>≣</button>
                                <div className="vr" style={{ height: '16px' }}></div>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>▼</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>🅰</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>A^</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>”</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>🔗</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>⃠</button>
                                <button type="button" className="btn btn-sm btn-light border-0 p-1" style={{ width: '28px', height: '28px', fontSize: '0.85rem' }}>⋯</button>
                            </div>
                        </div>
                        <textarea
                            ref={textareaRef}
                            className="form-control"
                            rows="5"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            style={{
                                resize: 'none',
                                borderRadius: '8px',
                                borderColor: '#006BB3',
                                backgroundColor: '#F8F9FA'
                            }}
                        ></textarea>
                    </div>

                    {/* Footer Buttons */}
                    <div className="d-flex justify-content-center gap-3">
                        <button
                            type="button"
                            className="btn"
                            style={{
                                borderRadius: '6px',
                                padding: '8px 40px',
                                fontWeight: '600',
                                backgroundColor: 'white',
                                borderColor: '#006BB3',
                                color: '#006BB3',
                                minWidth: '120px'
                            }}
                            onClick={onClose}
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn"
                            style={{
                                borderRadius: '6px',
                                padding: '8px 40px',
                                fontWeight: '600',
                                backgroundColor: '#006BB3',
                                borderColor: '#006BB3',
                                color: 'white',
                                minWidth: '120px'
                            }}
                            onClick={onSave}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};